// src/contexts/authContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (storedToken) {
        try {
          const decoded = jwtDecode(storedToken);
          
          if (decoded.exp * 1000 < Date.now()) {
            logout();
            return;
          }
          
          await fetchUserStatus(storedToken);
          
          setToken(storedToken);
          setIsNewUser(decoded.is_new_user || false);
          
          if (decoded.is_new_user) {
            navigate('/push-notification');
          }
        } catch (error) {
          console.error('Token validation failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const fetchUserStatus = async (token) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/home`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.current_user) {
        setUser(response.data.current_user);
        setIsAdmin(response.data.current_user.admin || false);
      }
    } catch (error) {
      console.error('Error fetching user status:', error);
      throw error;
    }
  };

  const login = (token, isNew = false) => {
    const decoded = jwtDecode(token);
    localStorage.setItem('token', token);
    
    setToken(token);
    setIsNewUser(isNew || decoded.is_new_user || false);
    //setIsVerified(decoded.is_verified || false);
    fetchUserStatus(token);
    
    navigate(isNew || decoded.is_new_user ? '/push-notification' : '/home');
  };

  const handleGoogleAuth = (googleData) => {
    login(googleData.token, googleData.is_new_user);
  };

  const handleNormalSignup = async (userData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/signup`, userData);
      localStorage.setItem('tempUser', JSON.stringify(userData));
      navigate('/verify');
      return response.data;
    } catch (error) {
      console.error('Signup failed:', error);
      throw error;
    }
  };

  const completeSignup = (token) => {
    localStorage.removeItem('tempUser');
    login(token, false);
    navigate('/push-notification');
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tempUser');
    setToken(null);
    setUser(null);
    setIsNewUser(false);
    setIsAdmin(false);
    navigate('/auth');
  };

  const value = {
    user,
    token,
    isNewUser,
    isAdmin,
    loading,
    login,
    logout,
    handleGoogleAuth,
    handleNormalSignup,
    completeSignup,
    fetchUserStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};